import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Building2,
  Briefcase,
  MapPin,
  Calendar,
  Award,
  CheckCircle,
  AlertCircle,
  Image as ImageIcon,
  Save,
  Upload,
  FileText,
  X,
  Move
} from 'lucide-react';

interface ConsultantProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  title: string;
  country_region: string;
  city?: string;
  state?: string;
  experience_years: string;
  workday_modules: string;
  workday_modules_list?: string[];
  certifications: string;
  experience_text: string;
  availability_status: string;
  availability?: string;
  hourly_rate: string;
  profile_complete: boolean;
  project_end_date?: string;
  resume_url?: string;
  photo_url?: string;
  photo_position_x?: number;
  photo_position_y?: number;
  project_preferences_list?: string[];
}

const ADMIN_DEMO_PROFILE: Partial<ConsultantProfile> = {
  first_name: 'Alex',
  last_name: 'Johnson',
  email: 'alex.johnson@example.com',
  phone: '+1 (555) 234-5678',
  company: 'Workday Solutions LLC',
  title: 'Senior Workday Consultant',
  country_region: 'United States',
  city: 'Dallas',
  state: 'Texas',
  experience_years: '5-10 years',
  workday_modules: 'HCM, Payroll, Financials, Recruiting',
  workday_modules_list: ['HCM', 'Payroll', 'Financials', 'Recruiting'],
  certifications: 'Workday HCM Core, Workday Payroll',
  experience_text: 'Senior Workday Consultant with 7+ years of experience implementing and optimizing Workday HCM and Payroll solutions for Fortune 500 companies. Led 12 full-cycle implementations across healthcare, finance, and technology sectors.',
  availability_status: 'available',
  availability: 'Immediate',
  hourly_rate: '$175/hour',
  profile_complete: true,
  resume_url: 'demo/resume.pdf',
  photo_url: '',
};

export default function Profile() {
  const navigate = useNavigate();
  const { user, loading: authLoading, userType } = useAuth();
  const { theme, setTheme, setThemeTransient } = useTheme();
  const isAdminPreview = userType === 'admin';
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    setThemeTransient('light');
  }, [setThemeTransient]);
  const [isLoadingLogo, setIsLoadingLogo] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [isAdjustingPhoto, setIsAdjustingPhoto] = useState(false);
  const [photoPos, setPhotoPos] = useState({ x: 50, y: 50 });
  const [isSavingPosition, setIsSavingPosition] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, posX: 50, posY: 50 });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [profile, setProfile] = useState<Partial<ConsultantProfile>>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    country_region: '',
    city: '',
    state: '',
    experience_years: '',
    workday_modules: '',
    workday_modules_list: [],
    certifications: '',
    experience_text: '',
    availability_status: 'available',
    availability: '',
    hourly_rate: '',
    project_end_date: '',
    project_preferences_list: []
  });

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
    fetchLogo();
  }, [user?.id]);

  const fetchLogo = async () => {
    try {
      setIsLoadingLogo(true);
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'logo_url')
        .maybeSingle();

      if (!error && data?.value) {
        const img = new Image();
        img.onload = () => {
          setLogoUrl(data.value);
          setIsLoadingLogo(false);
        };
        img.onerror = () => setIsLoadingLogo(false);
        img.src = data.value;
      } else {
        setIsLoadingLogo(false);
      }
    } catch (err) {
      setIsLoadingLogo(false);
    }
  };

  const fetchProfile = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('consultant_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setProfile(data);
      } else if (isAdminPreview) {
        // Admin has no consultant profile — load demo data so the page renders realistically
        setProfile(ADMIN_DEMO_PROFILE);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setMessage({ type: 'error', text: 'Failed to load profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      // Strip everything except digits and leading +
      const digits = value.replace(/[^\d+\-() ]/g, '');
      // Enforce max 15 digits (E.164 max), counting only digit characters
      const digitCount = digits.replace(/\D/g, '').length;
      if (digitCount > 15) return;
      setProfile(prev => ({ ...prev, phone: digits }));
      return;
    }
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    if (!profile.experience_text?.trim()) {
      setMessage({ type: 'error', text: 'Experience is required. Please paste your work experience from your resume.' });
      return;
    }

    if (isAdminPreview) {
      setMessage({ type: 'error', text: 'Admin preview — changes are not saved.' });
      return;
    }

    try {
      setSaving(true);
      setMessage(null);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { linkedin_url, ...profileToSave } = profile as any;
      const { error } = await supabase
        .from('consultant_profiles')
        .update({
          ...profileToSave,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      fetchProfile();
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const calculateCompleteness = () => {
    const fields = [
      profile.first_name,
      profile.last_name,
      profile.email,
      profile.phone,
      profile.title,
      profile.country_region,
      profile.experience_years,
      profile.workday_modules,
      profile.experience_text,
      profile.resume_url,
      profile.photo_url
    ];
    const filledFields = fields.filter(field => field && field.toString().trim() !== '').length;
    return Math.round((filledFields / fields.length) * 100);
  };

  const handleResumeUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Please upload a PDF, DOC, or DOCX file' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size must be less than 5MB' });
      return;
    }

    try {
      setUploading(true);
      setMessage(null);

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/resume.${fileExt}`;

      if (profile.resume_url) {
        const oldPath = profile.resume_url.split('/').slice(-2).join('/');
        await supabase.storage.from('resumes').remove([oldPath]);
      }

      const { error: uploadError } = await supabase.storage
        .from('resumes')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('resumes')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('consultant_profiles')
        .update({ resume_url: fileName })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setProfile(prev => ({ ...prev, resume_url: fileName }));
      setMessage({ type: 'success', text: 'Resume uploaded successfully!' });
      fetchProfile();
    } catch (error: any) {
      console.error('Error uploading resume:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to upload resume' });
    } finally {
      setUploading(false);
    }
  };

  const handleResumeDelete = async () => {
    if (!user?.id || !profile.resume_url) return;

    try {
      setUploading(true);
      setMessage(null);

      const { error: deleteError } = await supabase.storage
        .from('resumes')
        .remove([profile.resume_url]);

      if (deleteError) throw deleteError;

      const { error: updateError } = await supabase
        .from('consultant_profiles')
        .update({ resume_url: null })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setProfile(prev => ({ ...prev, resume_url: '' }));
      setMessage({ type: 'success', text: 'Resume deleted successfully!' });
      fetchProfile();
    } catch (error: any) {
      console.error('Error deleting resume:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to delete resume' });
    } finally {
      setUploading(false);
    }
  };

  const getResumeDownloadUrl = async () => {
    if (!profile.resume_url) return null;

    const { data } = await supabase.storage
      .from('resumes')
      .createSignedUrl(profile.resume_url, 60);

    return data?.signedUrl || null;
  };

  const handleResumeDownload = async () => {
    const url = await getResumeDownloadUrl();
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Please upload a JPG, PNG, or WebP image' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size must be less than 5MB' });
      return;
    }

    try {
      setUploadingPhoto(true);
      setMessage(null);

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/profile-photo.${fileExt}`;

      if (profile.photo_url) {
        const oldPath = profile.photo_url;
        await supabase.storage.from('profile-photos').remove([oldPath]);
      }

      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { error: updateError } = await supabase
        .from('consultant_profiles')
        .update({ photo_url: fileName })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setProfile(prev => ({ ...prev, photo_url: fileName }));
      setMessage({ type: 'success', text: 'Profile photo uploaded successfully!' });
      fetchProfile();
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to upload photo' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async () => {
    if (!user?.id || !profile.photo_url) return;

    try {
      setUploadingPhoto(true);
      setMessage(null);

      const { error: deleteError } = await supabase.storage
        .from('profile-photos')
        .remove([profile.photo_url]);

      if (deleteError) throw deleteError;

      const { error: updateError } = await supabase
        .from('consultant_profiles')
        .update({ photo_url: null })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setProfile(prev => ({ ...prev, photo_url: '' }));
      setMessage({ type: 'success', text: 'Profile photo deleted successfully!' });
      fetchProfile();
    } catch (error: any) {
      console.error('Error deleting photo:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to delete photo' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  useEffect(() => {
    if (profile.photo_position_x !== undefined && profile.photo_position_y !== undefined) {
      setPhotoPos({ x: profile.photo_position_x, y: profile.photo_position_y });
    }
  }, [profile.photo_position_x, profile.photo_position_y]);

  const handlePhotoDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    isDragging.current = true;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStart.current = { x: clientX, y: clientY, posX: photoPos.x, posY: photoPos.y };

    const onMove = (ev: MouseEvent | TouchEvent) => {
      if (!isDragging.current || !dragRef.current) return;
      const cx = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
      const cy = 'touches' in ev ? ev.touches[0].clientY : ev.clientY;
      const rect = dragRef.current.getBoundingClientRect();
      const dx = cx - dragStart.current.x;
      const dy = cy - dragStart.current.y;
      const newX = Math.min(100, Math.max(0, dragStart.current.posX - (dx / rect.width) * 100));
      const newY = Math.min(100, Math.max(0, dragStart.current.posY - (dy / rect.height) * 100));
      setPhotoPos({ x: Math.round(newX), y: Math.round(newY) });
    };
    const onUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }, [photoPos]);

  const handleSavePhotoPosition = async () => {
    if (!user?.id) return;
    setIsSavingPosition(true);
    try {
      const { error } = await supabase
        .from('consultant_profiles')
        .update({ photo_position_x: photoPos.x, photo_position_y: photoPos.y })
        .eq('id', user.id);
      if (error) throw error;
      setProfile(prev => ({ ...prev, photo_position_x: photoPos.x, photo_position_y: photoPos.y }));
      setIsAdjustingPhoto(false);
      setMessage({ type: 'success', text: 'Photo position saved!' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to save position' });
    } finally {
      setIsSavingPosition(false);
    }
  };

  const getPhotoUrl = () => {
    if (!profile.photo_url) return null;

    const { data } = supabase.storage
      .from('profile-photos')
      .getPublicUrl(profile.photo_url);

    return data.publicUrl;
  };

  const completeness = calculateCompleteness();

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    }`}>
      {/* Header */}
      <nav className="fixed w-full z-50 bg-black backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="text-white -mt-4 -mb-4 cursor-default select-none">
              {isLoadingLogo ? (
                <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : logoUrl ? (
                <img
                  src="/1.png"
                  alt="Perplexity"
                  className="h-40 w-auto object-contain"
                />
              ) : (
                <ImageIcon size={32} className="text-primary" />
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/consultant/view-profile')}
                className="flex items-center gap-2 px-4 py-2 bg-[#40E0D0] hover:bg-[#35c5b5] text-white text-sm font-semibold rounded-full transition-colors"
              >
                <User className="w-4 h-4" />
                View Profile
              </button>
              <button
                onClick={() => navigate('/consultant/dashboard')}
                className="text-white hover:text-primary transition-colors flex items-center"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Admin preview banner */}
          {isAdminPreview && (
            <div className="mb-6 flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm font-medium">
              <AlertCircle className="w-4 h-4 flex-shrink-0 text-amber-500" />
              Admin preview — showing demo data. Changes cannot be saved from this view.
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4">Your Profile</h1>
            <div className="flex items-center space-x-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                    Profile Completeness
                  </span>
                  <span className="font-semibold">{completeness}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${completeness}%` }}
                  ></div>
                </div>
              </div>
            </div>
            <div className={`p-4 rounded-lg border ${
              completeness === 100
                ? 'bg-green-50 border-green-200'
                : 'bg-blue-50 border-blue-200'
            }`}>
              <p className={`text-sm ${
                completeness === 100 ? 'text-green-700' : 'text-blue-700'
              }`}>
                {completeness === 100
                  ? 'Your profile is complete and visible to our clients. Clients can request interviews with you.'
                  : 'Please complete all required fields to make your profile viewable to our clients. Our clients are able to request an interview with you if you have 100% completed your profile. If you are selected for an interview we will contact you for your interview availability.'}
              </p>
            </div>
          </div>

          {/* Messages */}
          {message && (
            <div
              className={`mb-6 p-4 rounded-lg flex items-center ${
                message.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}
            >
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          {/* Profile Photo Section */}
          <div className={`rounded-xl p-6 mb-6 ${
            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
          }`}>
            <h2 className="text-2xl font-bold mb-4">Profile Photo</h2>
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Photo Preview / Adjust */}
              <div className="flex-shrink-0 flex flex-col items-center gap-3">
                {getPhotoUrl() ? (
                  isAdjustingPhoto ? (
                    <div className="flex flex-col items-center gap-2">
                      <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Drag to reposition
                      </p>
                      <div
                        ref={dragRef}
                        className="w-40 h-40 rounded-full overflow-hidden border-4 border-[#40E0D0] cursor-grab active:cursor-grabbing select-none relative"
                        onMouseDown={handlePhotoDragStart}
                        onTouchStart={handlePhotoDragStart}
                        title="Drag to adjust photo position"
                      >
                        <img
                          src={getPhotoUrl()!}
                          alt="Profile"
                          className="w-full h-full object-cover pointer-events-none"
                          style={{ objectPosition: `${photoPos.x}% ${photoPos.y}%` }}
                          draggable={false}
                        />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="bg-black/30 rounded-full p-2">
                            <Move className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleSavePhotoPosition}
                          disabled={isSavingPosition}
                          className="px-4 py-2 bg-[#40E0D0] hover:bg-[#35c5b5] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
                        >
                          {isSavingPosition ? 'Saving...' : 'Save Position'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setIsAdjustingPhoto(false); setPhotoPos({ x: profile.photo_position_x ?? 50, y: profile.photo_position_y ?? 50 }); }}
                          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="relative group">
                      <img
                        src={getPhotoUrl()!}
                        alt="Profile"
                        className="w-32 h-32 rounded-full object-cover border-4 border-[#40E0D0]"
                        style={{ objectPosition: `${photoPos.x}% ${photoPos.y}%` }}
                      />
                      <button
                        type="button"
                        onClick={handlePhotoDelete}
                        disabled={uploadingPhoto}
                        className="absolute top-0 right-0 bg-red-600 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                        title="Delete photo"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )
                ) : (
                  <div className={`w-32 h-32 rounded-full flex items-center justify-center border-4 ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-300'
                  }`}>
                    <User className={`w-16 h-16 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                )}
              </div>

              {/* Upload Instructions and Buttons */}
              <div className="flex-1">
                <h3 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {profile.photo_url ? 'Update your photo' : 'Add a profile photo'}
                </h3>
                <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  JPG, PNG, or WebP (Max 5MB). For best results use a square image.
                </p>
                <div className="flex flex-wrap gap-3">
                  <label className="inline-flex items-center px-5 py-2.5 bg-[#40E0D0] hover:bg-[#35c5b5] text-white font-medium rounded-lg transition-colors cursor-pointer">
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingPhoto ? 'Uploading...' : profile.photo_url ? 'Change Photo' : 'Upload Photo'}
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhoto}
                      className="hidden"
                    />
                  </label>
                  {getPhotoUrl() && !isAdjustingPhoto && (
                    <button
                      type="button"
                      onClick={() => setIsAdjustingPhoto(true)}
                      className={`inline-flex items-center px-5 py-2.5 font-medium rounded-lg border-2 transition-colors ${
                        theme === 'dark'
                          ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Move className="w-4 h-4 mr-2" />
                      Adjust Position
                    </button>
                  )}
                </div>
                {getPhotoUrl() && !isAdjustingPhoto && (
                  <p className={`text-xs mt-3 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                    Use "Adjust Position" if your photo appears cropped in the wrong spot.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Profile Form */}
          <form onSubmit={handleSubmit} className={`rounded-xl p-6 ${
            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
          }`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium mb-2">First Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="first_name"
                    value={profile.first_name || ''}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="John"
                  />
                </div>
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium mb-2">Last Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="last_name"
                    value={profile.last_name || ''}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="Doe"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={profile.email || ''}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium mb-2">Phone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={profile.phone || ''}
                    onChange={handleChange}
                    maxLength={20}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              {/* Company */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Company <span className="text-gray-400 text-xs">(Optional)</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="company"
                    value={profile.company || ''}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="Your Company"
                  />
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="title"
                    value={profile.title || ''}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="Workday Consultant"
                  />
                </div>
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium mb-2">City</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="city"
                    value={profile.city || ''}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="Dallas"
                  />
                </div>
              </div>

              {/* State / Region */}
              <div>
                <label className="block text-sm font-medium mb-2">State / Region</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="state"
                    value={profile.state || ''}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="Texas"
                  />
                </div>
              </div>

              {/* Experience Years */}
              <div>
                <label className="block text-sm font-medium mb-2">Years of Experience</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    name="experience_years"
                    value={profile.experience_years || ''}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-primary`}
                  >
                    <option value="">Select experience</option>
                    <option value="1-2 years">1-2 years</option>
                    <option value="3-5 years">3-5 years</option>
                    <option value="5-10 years">5-10 years</option>
                    <option value="10+ years">10+ years</option>
                  </select>
                </div>
              </div>

              {/* Workday Modules */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Workday Modules & Functional Expertise</label>
                {profile.workday_modules_list && profile.workday_modules_list.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {profile.workday_modules_list.map((mod, i) => (
                      <span
                        key={i}
                        className={`text-sm px-3 py-1.5 rounded-full font-medium border ${
                          theme === 'dark'
                            ? 'bg-primary/10 text-primary border-primary/30'
                            : 'bg-primary/10 text-[#1a9e90] border-primary/40'
                        }`}
                      >
                        {mod}
                      </span>
                    ))}
                  </div>
                ) : null}
                <textarea
                  name="workday_modules"
                  value={profile.workday_modules || ''}
                  onChange={handleChange}
                  rows={2}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-primary`}
                  placeholder="Additional modules or expertise not listed above..."
                />
              </div>

              {/* Project Preferences */}
              {profile.project_preferences_list && profile.project_preferences_list.length > 0 && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Project Preferences</label>
                  <div className="flex flex-wrap gap-2">
                    {profile.project_preferences_list.map((pref, i) => (
                      <span
                        key={i}
                        className={`text-sm px-3 py-1.5 rounded-full font-medium border ${
                          theme === 'dark'
                            ? 'bg-blue-900/30 text-blue-300 border-blue-700/40'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }`}
                      >
                        {pref}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  Certifications <span className="text-gray-400 text-xs">(Optional)</span>
                </label>
                <div className="relative">
                  <Award className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <textarea
                    name="certifications"
                    value={profile.certifications || ''}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="List your Workday certifications"
                  />
                </div>
              </div>

              {/* Experience */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">
                  Experience <span className="text-red-500">*</span>
                </label>
                <p className={`text-xs mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  This information will be used in job applications and shared with clients once your profile is complete.
                </p>
                <textarea
                  name="experience_text"
                  value={profile.experience_text || ''}
                  onChange={handleChange}
                  rows={8}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  } focus:outline-none focus:ring-2 focus:ring-primary`}
                  placeholder="Paste your work experience from your resume here. Do not include your name. This information will be used in job applications and shared with clients once your profile is complete."
                  required
                />
              </div>

              {/* Resume Upload */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Resume</label>
                {profile.resume_url ? (
                  <div className={`flex items-center justify-between p-4 rounded-lg border ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600'
                      : 'bg-gray-50 border-gray-300'
                  }`}>
                    <div className="flex items-center space-x-3">
                      <FileText className="w-8 h-8 text-primary" />
                      <div>
                        <p className="font-medium">Resume Uploaded</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {profile.resume_url.split('/').pop()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={handleResumeDownload}
                        className="px-4 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-black transition-colors"
                      >
                        View
                      </button>
                      <button
                        type="button"
                        onClick={handleResumeDelete}
                        disabled={uploading}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center ${
                    theme === 'dark'
                      ? 'border-gray-600 bg-gray-700/50'
                      : 'border-gray-300 bg-gray-50'
                  }`}>
                    <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className={`mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Upload your resume
                    </p>
                    <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      PDF, DOC, or DOCX (Max 5MB)
                    </p>
                    <label className="inline-flex items-center px-4 py-2 bg-primary text-black font-medium rounded-lg hover:bg-primary/90 transition-colors cursor-pointer">
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Choose File'}
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={handleResumeUpload}
                        disabled={uploading}
                        className="hidden"
                      />
                    </label>
                  </div>
                )}
              </div>

              {/* Availability notice */}
              <div>
                <label className="block text-sm font-medium mb-2">Show Availability Notice</label>
                <select
                  name="availability"
                  value={profile.availability || ''}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-primary`}
                >
                  <option value="">Select availability notice</option>
                  <option value="Immediate">Immediate</option>
                  <option value="2 Weeks Notice">2 Weeks Notice</option>
                  <option value="1 Month Notice">1 Month Notice</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>

              {/* Availability Status */}
              <div>
                <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block"></span>
                  Available for work:
                </label>
                <select
                  name="availability_status"
                  value={profile.availability_status || 'available'}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-primary`}
                >
                  <option value="available">Available</option>
                  <option value="on_project">On a project</option>
                  <option value="unavailable">Unavailable</option>
                </select>
              </div>

              {/* Project End Date - Only show when "On a project" is selected */}
              {profile.availability_status === 'on_project' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Project End Date</label>
                  <input
                    type="date"
                    name="project_end_date"
                    value={profile.project_end_date || ''}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-primary`}
                  />
                </div>
              )}

              {/* Hourly Rate - Only show when NOT "On a project" */}
              {profile.availability_status !== 'on_project' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Hourly Rate</label>
                  <input
                    type="text"
                    name="hourly_rate"
                    value={profile.hourly_rate || ''}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="$150/hour"
                  />
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="mt-8">
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-primary text-black font-semibold py-3 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-black mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Save Profile
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
